import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import { HideOnDesktop, HideOnMobile } from '@salesforce/retail-react-app/app/components/responsive'
import {
    Icon,
    Box,
    Text,
    VStack,
    HStack,
    Button,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { FisBack, CustomClose } from '../../icons'
import { formatOpeningHours } from '../../../helpers/storesHelper'
import { FocusTrap } from 'focus-trap-react';

/* Context */
import { useStoreLocator } from '../../../contexts/store-locator-context';

const StoreDetailView = ({ stores, onFindInStoreClose, selectedStore, setSelectedStore, availabilityText, availabilityColor }) => {
    const intl = useIntl()
    const isDesktop = useBreakpointValue({ base: false, md: true })
    const {
        selectedStore: mapSelectedStore,
    } = useStoreLocator()

    const handleGetDirections = (address) => {
        const destination = encodeURIComponent(
            `${address.address1}, ${address.city}, ${address.state_code} ${address.postal_code}`
        )
        const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`

        window.open(url, '_blank')
    }

    const handleBack = () => {
        setSelectedStore(null)
    }

    useEffect(() => {
        if (isDesktop && stores) {
            setSelectedStore(stores?.find((store) => store?.store_id === mapSelectedStore?.id))
        }
    }, [mapSelectedStore])

    if (!selectedStore) {
        return
    }

    const { name, address, distance, distance_unit, parkingLotAvailable } =
        selectedStore

    return (
        <FocusTrap>
            <Box
                position="fixed"
                top={0}
                right={0}
                width="100%"
                maxWidth={{ md: "483px" }}
                height="100vh"
                bg="white"
                zIndex={20}
                p={{ base: 4, lg: 6 }}
                overflowY="auto"
            >
                <HStack justifyContent="space-between" mb={4}>
                    <Button
                        onClick={handleBack}
                        _hover={{ bg: 'none' }}
                        px={0}
                        size="sm"
                        border={0}
                        color="black"
                        backgroundColor="white"
                    >
                        <Icon as={FisBack} />
                    </Button>

                    <Button
                        onClick={onFindInStoreClose}
                        _hover={{ bg: 'none' }}
                        px={0}
                        size="sm"
                        border={0}
                        color="black"
                        backgroundColor="white"
                    >
                        <Icon as={CustomClose} />
                    </Button>
                </HStack>

                <HStack mb={2} justifyContent="space-between">
                    <Text fontSize="lg" fontWeight="bold" textTransform="uppercase">
                        {name}
                    </Text>
                    <Text fontSize="xs">
                        {distance} {distance_unit} away
                    </Text>
                </HStack>

                <Text fontSize="sm">
                    {address.address1}
                    <br />
                    {`${address.city}, ${address.state_code}, ${address.postal_code}`}
                    <br />
                    {address.phone}
                </Text>

                <HStack mt={2} justifyContent="space-between">
                    <HStack>
                        <Box w="6px" h="6px" bg={availabilityColor} />
                        <Text fontSize="xs">{availabilityText}</Text>
                    </HStack>

                    <HideOnMobile>
                        <Button
                            fontSize="14px"
                            fontWeight="bold"
                            border={0}
                            variant="link"
                            color="black"
                            textTransform="uppercase"
                            onClick={() => handleGetDirections(address)}
                        >
                            <FormattedMessage
                                id="pdp_find_in_store.directions"
                                defaultMessage="Get Directions"
                            />
                        </Button>
                    </HideOnMobile>
                </HStack>

                <Box py={8} fontSize="md">
                    <VStack align="start" mb={8} gap={0} textTransform="uppercase">
                        <Text mb={2} fontSize="lg" fontWeight="bold" textTransform="uppercase">
                            <FormattedMessage
                                id="pdp_find_in_store.opening.hours"
                                defaultMessage="Opening Hours"
                            />
                        </Text>
                        {formatOpeningHours(selectedStore.openingHours).map((entry, index) => (
                            <Text key={index}>
                                <b>{entry.days}</b> {entry.timeRange}
                            </Text>
                        ))}

                        <HideOnDesktop width="100%">
                            <HStack mt={2} gap={2}>
                                <Button
                                    as="a"
                                    variant="outline"
                                    borderColor="black"
                                    color="black"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    padding="13px"
                                    fontSize="md"
                                    lineHeight="5"
                                    height="auto"
                                    _hover={{ bg: 'gray.50' }}
                                    width="100%"
                                    href={`tel:${address.phone}`}
                                >
                                    <FormattedMessage
                                        id="pdp_find_in_store.call"
                                        defaultMessage="Call Store"
                                    />
                                </Button>

                                <Button
                                    bg="black"
                                    width="100%"
                                    color="white"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    padding="14px"
                                    fontSize="md"
                                    lineHeight="5"
                                    height="auto"
                                    _hover={{ bg: 'blackAlpha.800' }}
                                    onClick={() => handleGetDirections(address)}
                                >
                                    <FormattedMessage
                                        id="pdp_find_in_store.directions"
                                        defaultMessage="Get Directions"
                                    />
                                </Button>
                            </HStack>
                        </HideOnDesktop>
                    </VStack>

                    <VStack align="start" mb={8} gap={0}>
                        <Text mb={2} fontSize="lg" fontWeight="bold" textTransform="uppercase">
                            <FormattedMessage
                                id="pdp_find_in_store.holiday"
                                defaultMessage="Store Holidays"
                            />
                        </Text>
                        {selectedStore.storeHolidays.length > 0 ? (
                            selectedStore.storeHolidays.map((holiday, index) => (
                                <Text key={index}>{holiday}</Text>
                            ))
                        ) : (
                            <Text>
                                <FormattedMessage
                                    id="pdp_find_in_store.no.holiday"
                                    defaultMessage="No holidays listed"
                                />
                            </Text>
                        )}
                    </VStack>

                    <VStack align="start" gap={0}>
                        <Text mb={2} fontSize="lg" fontWeight="bold" textTransform="uppercase">
                            <FormattedMessage
                                id="pdp_find_in_store.parking"
                                defaultMessage="Parking Lot"
                            />
                        </Text>
                        <Text>
                            {parkingLotAvailable
                                ? intl.formatMessage({
                                    id: 'pdp_find_in_store.parking.available',
                                    defaultMessage: 'Parking lot available'
                                })
                                : intl.formatMessage({
                                    id: 'pdp_find_in_store.parking.unavailable',
                                    defaultMessage: 'No parking lot available'
                                })}
                        </Text>
                    </VStack>
                </Box>
            </Box>
        </FocusTrap>
    );
}

StoreDetailView.propTypes = {
    stores: PropTypes.array,
    onFindInStoreClose: PropTypes.func,
    selectedStore: PropTypes.object,
    setSelectedStore: PropTypes.func,
    availabilityText: PropTypes.string,
    availabilityColor: PropTypes.string
}

export default StoreDetailView
